import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import {AssigneeRepositoryPicker, type AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {ProjectPickerBaseProject} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import {MilestoneIcon, PeopleIcon, ProjectSymlinkIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {useCallback, useMemo} from 'react'

import type {RepoPullsBulkSelectionValue, RepoPullsBulkUpdate} from '../helpers/repo-pulls-bulk-update'

interface RepoPullsBulkMetadataProps {
  disabled: boolean
  nested: boolean
  owner: string
  repo: string
  onUpdate: (update: RepoPullsBulkUpdate) => void
}

interface RepoPullsBulkAssigneesProps extends RepoPullsBulkMetadataProps {
  commonAssigneeLogins: string[]
}

function accessibleAnchorProps(anchorProps: React.HTMLAttributes<HTMLElement>, disabled: boolean) {
  return disabled
    ? {...anchorProps, 'aria-disabled': true, onClick: (event: React.MouseEvent<HTMLElement>) => event.preventDefault()}
    : anchorProps
}

export function RepoPullsBulkAssignees({
  commonAssigneeLogins,
  disabled,
  nested,
  owner,
  repo,
  onUpdate,
}: RepoPullsBulkAssigneesProps) {
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) =>
      nested ? (
        <ActionList.Item {...accessibleAnchorProps(anchorProps, disabled)} role="menuitem">
          <ActionList.LeadingVisual>
            <PeopleIcon />
          </ActionList.LeadingVisual>
          Assign
        </ActionList.Item>
      ) : (
        <Button
          leadingVisual={PeopleIcon}
          trailingVisual={TriangleDownIcon}
          {...accessibleAnchorProps(anchorProps, disabled)}
        >
          Assign
        </Button>
      ),
    [disabled, nested],
  )
  const handleSelectionChange = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      const selectedLogins = new Set(selectedAssignees.map(assignee => assignee.login))
      const commonLogins = new Set(commonAssigneeLogins)
      const assigneeLogins: Record<string, RepoPullsBulkSelectionValue> = {}

      for (const login of selectedLogins) {
        if (!commonLogins.delete(login)) assigneeLogins[login] = '1'
      }
      for (const login of commonLogins) assigneeLogins[login] = '0'

      if (Object.keys(assigneeLogins).length > 0) onUpdate({assignee_logins: assigneeLogins})
    },
    [commonAssigneeLogins, onUpdate],
  )

  return (
    <AssigneeRepositoryPicker
      anchorElement={anchorElement}
      title="Apply assignees"
      repo={repo}
      owner={owner}
      readonly={disabled}
      onSelectionChange={handleSelectionChange}
      assignees={[]}
      assigneeTokens={commonAssigneeLogins}
      includeAssignableBots
      includeAuthorableBots={false}
      viewer={null}
      nested={nested}
      responsiveOnNarrow
    />
  )
}

export function RepoPullsBulkMilestone({disabled, nested, owner, repo, onUpdate}: RepoPullsBulkMetadataProps) {
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) =>
      nested ? (
        <ActionList.Item {...accessibleAnchorProps(anchorProps, disabled)} role="menuitem">
          <ActionList.LeadingVisual>
            <MilestoneIcon />
          </ActionList.LeadingVisual>
          Milestone
        </ActionList.Item>
      ) : (
        <Button
          leadingVisual={MilestoneIcon}
          trailingVisual={TriangleDownIcon}
          {...accessibleAnchorProps(anchorProps, disabled)}
        >
          Milestone
        </Button>
      ),
    [disabled, nested],
  )
  const noMilestoneItem = useMemo<ExtendedItemProps<MilestonePickerMilestone>>(
    () => ({
      id: SPECIAL_VALUES.noMilestoneData.id,
      description: '',
      descriptionVariant: 'inline',
      children: SPECIAL_VALUES.noMilestoneData.title,
      source: {
        id: SPECIAL_VALUES.noMilestoneData.id,
        title: SPECIAL_VALUES.noMilestoneData.title,
        closed: false,
        closedAt: null,
        dueOn: null,
        progressPercentage: 0,
        url: '',
      },
      groupId: '',
      leadingVisual: () => <MilestoneIcon />,
    }),
    [],
  )
  const handleSelectionChange = useCallback(
    (milestones: MilestonePickerMilestone[]) => {
      const milestone = milestones[0]
      if (!milestone) return
      onUpdate(milestone.id === SPECIAL_VALUES.noMilestoneData.id ? {milestone: ''} : {milestone_node_id: milestone.id})
    },
    [onUpdate],
  )

  return (
    <MilestonePicker
      anchorElement={anchorElement}
      title="Apply milestone"
      repo={repo}
      owner={owner}
      readonly={disabled}
      activeMilestone={null}
      noMilestoneItem={noMilestoneItem}
      onSelectionChange={handleSelectionChange}
      canCreateMilestone={false}
      nested={nested}
    />
  )
}

export function RepoPullsBulkProjects({disabled, nested, owner, repo, onUpdate}: RepoPullsBulkMetadataProps) {
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) =>
      nested ? (
        <ActionList.Item {...accessibleAnchorProps(anchorProps, disabled)} role="menuitem">
          <ActionList.LeadingVisual>
            <ProjectSymlinkIcon />
          </ActionList.LeadingVisual>
          Project
        </ActionList.Item>
      ) : (
        <Button
          leadingVisual={ProjectSymlinkIcon}
          trailingVisual={TriangleDownIcon}
          {...accessibleAnchorProps(anchorProps, disabled)}
        >
          Project
        </Button>
      ),
    [disabled, nested],
  )
  const handleSave = useCallback(
    (projects: ProjectPickerBaseProject[]) => {
      if (projects.length > 0) onUpdate({project_node_ids: projects.map(project => project.id)})
    },
    [onUpdate],
  )

  return (
    <ProjectPicker
      pickerId="repo-pulls-bulk-projects"
      anchorElement={anchorElement}
      title="Add to projects"
      repo={repo}
      owner={owner}
      readonly={disabled}
      selectedProjects={[]}
      onSave={handleSave}
      nested={nested}
    />
  )
}
